import { Slot } from '@radix-ui/react-slot';
import type { ComponentProps, ReactNode } from 'react';
import { buttonClass, type ButtonSize, type ButtonVariant } from './button-class';
import { Spinner } from './Spinner';
import { Tooltip } from './Tooltip';

export interface ButtonProps extends ComponentProps<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  asChild?: boolean;
  /** Leading icon; replaced by a spinner while loading. */
  icon?: ReactNode;
  /**
   * Icon-only buttons (size `icon`, `icon-sm`) show their `aria-label` as a
   * tooltip; pass other content, or `false` where a tooltip would only repeat
   * what is visible next to the button.
   */
  tooltip?: ReactNode | false;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  asChild = false,
  icon,
  className,
  children,
  disabled,
  type,
  tooltip,
  ...props
}: ButtonProps) {
  const classes = buttonClass(variant, size, className);
  if (asChild) {
    return (
      <Slot className={classes} {...props}>
        {children}
      </Slot>
    );
  }
  const button = (
    <button
      type={type ?? 'button'}
      className={classes}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Spinner className="size-3.5" /> : icon}
      {children}
    </button>
  );
  const iconOnly = size === 'icon' || size === 'icon-sm';
  const label = tooltip === false ? null : (tooltip ?? (iconOnly ? props['aria-label'] : null));
  return label ? <Tooltip content={label}>{button}</Tooltip> : button;
}
